<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              :title="isDeletePage() != true ? 'Doctor Management' : 'Deleted Doctors'"
              :subtitle="isDeletePage() != true ? 'Manage and monitor all doctors in the system' : 'Restore or permanently delete doctors'"
            >
              <template v-slot:actions>
                <div class="d-flex justify-end align-center">
                    <template v-if="isDeletePage() != true">
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            color="grey darken-1"
                            outlined
                            small
                            class="mr-2 px-3"
                            v-bind="attrs"
                            v-on="on"
                            @click="openTrash"
                          >
                            <v-icon x-small>mdi-trash-can-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>View Deleted Doctors</span>
                      </v-tooltip>
                      <v-btn
                        v-if="users.length > 0"
                        color="success"
                        outlined
                        small
                        class="mr-2 px-3"
                        :loading="isExcelLoading"
                        @click="generateExcel"
                      >
                        <v-icon left x-small>mdi-microsoft-excel</v-icon>
                        Export
                      </v-btn>
                      <v-btn
                        color="primary"
                        elevation="1"
                        small
                        to="/doctor/create"
                        class="mr-2 px-3"
                      >
                        <v-icon left x-small>mdi-plus</v-icon>
                        Add Doctor
                      </v-btn>
                    </template>
                    <v-btn
                      v-else
                      color="grey darken-1"
                      outlined
                      small
                      class="mr-2 px-3"
                      @click="closeTrash"
                    >
                      <v-icon left x-small>mdi-arrow-left</v-icon>
                      Back to Doctors
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Doctors</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="pendingVerificationCount > 0"
                      color="orange"
                      dark
                      x-small
                      class="mr-2"
                      style="cursor:pointer"
                      @click="filterByPending"
                    >
                      <v-icon left x-small>mdi-shield-alert</v-icon>
                      {{ pendingVerificationCount }} Pending Verification
                    </v-chip>
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Doctors"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    placeholder="Name, email, phone..."
                    hide-details
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.is_active"
                    :items="statuses"
                    label="Status"
                    prepend-inner-icon="mdi-toggle-switch"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Verification Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.is_verified"
                    :items="verificationOptions"
                    label="Verification"
                    prepend-inner-icon="mdi-shield-check"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Doctors Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-doctor</v-icon
                    >
                    <span class="text-body-1 font-weight-medium"
                      >Doctors List</span
                    >
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-doctor</v-icon>
                      {{ count }} {{ count === 1 ? "Doctor" : "Doctors" }}
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-2">
                      <span class="text-caption grey--text mr-1">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn x-small outlined :value="10">10</v-btn>
                        <v-btn x-small outlined :value="25">25</v-btn>
                        <v-btn x-small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <section
                v-if="isLoading"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <health-loader message="Loading doctors..." />
                </div>
              </section>

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="users"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="enhanced-table"
              >
                <!-- Serial Number Column -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- Status Column -->
                <template #[`item.is_active`]="{ item }">
                  <v-chip
                    :color="item.is_active ? 'success' : 'error'"
                    small
                    dark
                  >
                    <v-icon left x-small>
                      {{
                        item.is_active ? "mdi-check-circle" : "mdi-close-circle"
                      }}
                    </v-icon>
                    {{ item.is_active ? "Active" : "Inactive" }}
                  </v-chip>
                </template>

                <!-- Verification Column -->
                <template #[`item.verification`]="{ item }">
                  <div class="d-flex flex-column align-center">
                    <status-chip x-small :color="item.is_verified ? 'teal' : 'orange'" :icon="item.is_verified ? 'mdi-shield-check' : 'mdi-shield-alert'" :label="String(item.is_verified ? 'Verified' : 'Pending')" />
                  </div>
                </template>

                <!-- Avatar & Name Column -->
                <template #[`item.user_info`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-2">
                      <v-img
                        v-if="item.image_path"
                        :src="getImageUrl(item.image_path)"
                        :alt="item.first_name"
                      ></v-img>
                      <v-icon v-else color="grey lighten-1" size="18"
                        >mdi-doctor</v-icon
                      >
                    </v-avatar>
                    <div>
                      <div class="text-body-2 font-weight-medium">
                        {{ getFullName(item) }}
                      </div>
                      <div class="text-caption grey--text">
                        {{ item.user_name || "No username" }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Contact Column -->
                <template #[`item.contact`]="{ item }">
                  <div>
                    <div class="d-flex align-center" v-if="item.email">
                      <v-icon x-small class="mr-1 grey--text">mdi-email</v-icon>
                      <span class="text-caption">{{ item.email }}</span>
                    </div>
                    <div class="d-flex align-center mt-1" v-if="item.phone">
                      <v-icon x-small class="mr-1 grey--text">mdi-phone</v-icon>
                      <span class="text-caption">{{ item.phone }}</span>
                    </div>
                    <div
                      v-if="!item.email && !item.phone"
                      class="text-caption grey--text"
                    >
                      No contact
                    </div>
                  </div>
                </template>

                <!-- Role & Position Column -->
                <template #[`item.role_position`]="{ item }">
                  <div>
                    <v-chip
                      v-if="item.role"
                      color="info"
                      x-small
                      outlined
                      class="mb-1"
                    >
                      {{ item.role.name }}
                    </v-chip>
                    <div class="text-caption grey--text" v-if="item.position">
                      {{ item.position.name }}
                    </div>
                  </div>
                </template>

                <!-- Wallet Column -->
                <template #[`item.wallet`]="{ item }">
                  <v-chip :color="getWalletColor(item.wallet)" x-small dark>
                    <v-icon left x-small>mdi-wallet</v-icon>
                    ₹{{ formatCurrency(item.wallet) }}
                  </v-chip>
                </template>

                <!-- Actions Column -->
                <template #[`item.actions`]="{ item }">
                  <div class="d-flex justify-center">
                    <template v-if="!isDeletePage()">
                      <!-- View Details Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="success"
                            :to="`/doctors/${encryptIt(item.id)}/details`"
                            class="mr-1"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-account-details</v-icon>
                          </v-btn>
                        </template>
                        <span>View Details</span>
                      </v-tooltip>

                      <!-- Approve Button (only for inactive doctors) -->
                      <v-tooltip v-if="!item.is_active" bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="teal"
                            class="mr-1"
                            v-bind="attrs"
                            v-on="on"
                            @click="openVerifyDialog(item)"
                          >
                            <v-icon x-small>mdi-shield-check</v-icon>
                          </v-btn>
                        </template>
                        <span>Approve Profile</span>
                      </v-tooltip>

                      <!-- Edit Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="primary"
                            :to="`/doctor/${encryptIt(item.id)}`"
                            class="mr-1"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                        <span>Edit Doctor</span>
                      </v-tooltip>

                      <!-- Delete Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="error"
                            @click="openDeleteConfirm(item)"
                            v-bind="attrs"
                            v-on="on"
                            class="ml-1"
                          >
                            <v-icon x-small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>Delete Doctor</span>
                      </v-tooltip>
                    </template>

                    <template v-else>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="success"
                            @click="openRestoreConfirm(item)"
                            v-bind="attrs"
                            v-on="on"
                            class="mr-1"
                          >
                            <v-icon x-small left>mdi-restore</v-icon>
                            Restore
                          </v-btn>
                        </template>
                        <span>Restore this doctor</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="error"
                            @click="openPermanentDeleteConfirm(item)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small left>mdi-delete-forever</v-icon>
                            Permanent Delete
                          </v-btn>
                        </template>
                        <span>Permanently delete this doctor — cannot be undone</span>
                      </v-tooltip>
                    </template>
                  </div>
                </template>

                <!-- Empty State -->
                <template #no-data>
                  <div class="text-center py-12">
                    <v-icon size="80" color="grey lighten-3"
                      >mdi-doctor</v-icon
                    >
                    <h3 class="text-h6 grey--text mt-4 mb-2">No Doctors Found</h3>
                    <p class="grey--text mb-4">
                      {{
                        hasActiveFilters
                          ? "No doctors match your current filters. Try adjusting your search criteria."
                          : "You haven't added any doctors yet."
                      }}
                    </p>
                    <v-btn
                      v-if="!hasActiveFilters && !isDeletePage()"
                      color="primary"
                      to="/users/create"
                    >
                      <v-icon left>mdi-plus</v-icon>
                      Add Your First Doctor
                    </v-btn>
                    <v-btn
                      v-else-if="hasActiveFilters"
                      color="primary"
                      outlined
                      @click="clearAllFilters"
                    >
                      <v-icon left>mdi-filter-remove</v-icon>
                      Clear Filters
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Integrated Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-caption grey--text">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <!-- Previous Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page <= 1"
                      @click="page--"
                      class="mr-2"
                    >
                      <v-icon x-small left>mdi-chevron-left</v-icon>
                      Prev
                    </v-btn>

                    <!-- Page Numbers -->
                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="4"
                      circle
                      class="compact-pagination mx-1"
                    ></v-pagination>

                    <!-- Next Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page >= paginationLength"
                      @click="page++"
                      class="ml-2"
                    >
                      Next
                      <v-icon x-small right>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>

                <!-- Jump to Page -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-caption grey--text mr-2">Go to:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :min="1"
                      :max="paginationLength"
                      dense
                      outlined
                      hide-details
                      hide-spin-buttons
                      class="no-spin-arrows compact-input"
                      style="max-width: 60px"
                      @keydown.enter="goToPage"
                      @blur="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="confirmDeleteDialog" max-width="460" persistent>
        <v-card>
          <v-card-title class="error white--text py-3 px-4">
            <v-icon color="white" class="mr-2">mdi-delete-alert</v-icon>
            Confirm Delete
          </v-card-title>
          <v-card-text class="pt-4 pb-2" v-if="deleteTargetItem">
            <p class="text-body-1 mb-3">Are you sure you want to delete this doctor? This action cannot be undone.</p>
            <v-divider class="mb-3"></v-divider>
            <div class="d-flex align-center mb-2">
              <v-icon small class="mr-2 grey--text">mdi-account</v-icon>
              <span class="font-weight-medium">{{ deleteTargetItem.first_name }} {{ deleteTargetItem.last_name }}</span>
            </div>
            <div class="d-flex align-center mb-2" v-if="deleteTargetItem.email">
              <v-icon small class="mr-2 grey--text">mdi-email</v-icon>
              <span class="text-body-2">{{ deleteTargetItem.email }}</span>
            </div>
            <div class="d-flex align-center mb-2" v-if="deleteTargetItem.phone">
              <v-icon small class="mr-2 grey--text">mdi-phone</v-icon>
              <span class="text-body-2">{{ deleteTargetItem.phone }}</span>
            </div>
          </v-card-text>
          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn text @click="confirmDeleteDialog = false" :disabled="delete_user">Cancel</v-btn>
            <v-btn color="error" depressed :loading="delete_user" @click="Delete_this(deleteTargetItem.id)">
              <v-icon left small>mdi-delete</v-icon>
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <DeleteConfirmationDialog
        v-model="deleteDialog"
        :item="itemToDelete"
        :loading="deleteLoading"
        @confirm="confirmDelete"
        @cancel="deleteDialog = false"
      />
      <!-- Verify Doctor Dialog -->
      <v-dialog v-model="showVerifyDialog" max-width="480" persistent>
        <v-card v-if="doctorToVerify">
          <v-card-title class="headline py-3 px-4 d-flex align-center"
            :class="doctorToVerify.is_active ? 'warning white--text' : 'teal white--text'">
            <v-icon color="white" class="mr-2">
              {{ doctorToVerify.is_active ? 'mdi-shield-off' : 'mdi-shield-check' }}
            </v-icon>
            {{ doctorToVerify.is_active ? 'Deactivate Doctor' : 'Approve Doctor Profile' }}
          </v-card-title>

          <v-card-text class="pt-4">
            <div style="background:#f5f5f5;border-radius:8px" class="pa-3 mb-3">
              <div class="d-flex justify-space-between mb-1">
                <span class="text-caption grey--text">Name</span>
                <span class="text-caption font-weight-bold">
                  {{ ((doctorToVerify.first_name || '') + ' ' + (doctorToVerify.last_name || '')).trim() || 'N/A' }}
                </span>
              </div>
              <div class="d-flex justify-space-between mb-1">
                <span class="text-caption grey--text">Phone</span>
                <span class="text-caption font-weight-bold">{{ doctorToVerify.phone || 'N/A' }}</span>
              </div>
              <div class="d-flex justify-space-between mb-1">
                <span class="text-caption grey--text">Current Status</span>
                <status-chip x-small :color="doctorToVerify.is_active ? 'success' : 'error'" :label="String(doctorToVerify.is_active ? 'Active' : 'Inactive')" />
              </div>
              <div class="d-flex justify-space-between">
                <span class="text-caption grey--text">Profile Verified</span>
                <status-chip x-small :color="doctorToVerify.is_verified ? 'teal' : 'orange'" :label="String(doctorToVerify.is_verified ? 'Verified' : 'Pending')" />
              </div>
            </div>

            <v-textarea
              v-model="verifyRemarks"
              outlined
              dense
              label="Remarks (optional)"
              rows="2"
              hide-details="auto"
              class="mb-2"
            ></v-textarea>

            <p class="text-body-2 grey--text mb-0">
              <template v-if="!doctorToVerify.is_active">
                <strong>Approving</strong> will activate this doctor's account, allowing them to log into the Doctor App.
              </template>
              <template v-else>
                <strong>Deactivating</strong> will prevent this doctor from accessing the Doctor App.
              </template>
            </p>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn text color="grey darken-1" @click="closeVerifyDialog" :disabled="isVerifying">
              <v-icon left>mdi-close</v-icon>Cancel
            </v-btn>
            <v-btn
              :color="doctorToVerify.is_active ? 'warning' : 'teal'"
              dark
              @click="submitVerify"
              :loading="isVerifying"
            >
              <v-icon left>{{ doctorToVerify.is_active ? 'mdi-shield-off' : 'mdi-shield-check' }}</v-icon>
              {{ doctorToVerify.is_active ? 'Deactivate' : 'Approve' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Restore Confirmation Dialog -->
      <v-dialog v-model="showRestoreDialog" max-width="420" persistent>
        <v-card>
          <v-card-title class="success--text py-3 px-4">
            <v-icon color="success" left>mdi-restore</v-icon>
            Restore Doctor
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pt-4 pb-2 px-4">
            <p class="mb-2">This doctor will be restored and become active again:</p>
            <div v-if="restoreTarget" class="grey lighten-4 rounded pa-3 mb-2">
              <div><strong>{{ (restoreTarget.first_name || '') + ' ' + (restoreTarget.last_name || '') || restoreTarget.user_name }}</strong></div>
              <div class="text-caption grey--text">{{ restoreTarget.email }}</div>
              <div class="text-caption grey--text">{{ restoreTarget.phone }}</div>
            </div>
            <v-alert v-if="restoreError" dense text type="error" class="mt-3 mb-0">{{ restoreError }}</v-alert>
          </v-card-text>
          <v-card-actions class="px-4 pb-3">
            <v-spacer></v-spacer>
            <v-btn text @click="showRestoreDialog = false; restoreTarget = null; restoreError = ''" :disabled="isRestoring">Cancel</v-btn>
            <v-btn color="success" @click="confirmRestore" :loading="isRestoring">
              <v-icon left small>mdi-restore</v-icon>
              Restore
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Permanent Delete Confirmation Dialog -->
      <v-dialog v-model="showPermanentDeleteDialog" max-width="420" persistent>
        <v-card>
          <v-card-title class="error--text py-3 px-4">
            <v-icon color="error" left>mdi-delete-forever</v-icon>
            Permanently Delete Doctor
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pt-4 pb-2 px-4">
            <p class="mb-2">This action <strong>cannot be undone</strong>. The following doctor will be permanently removed:</p>
            <div v-if="permanentDeleteTarget" class="grey lighten-4 rounded pa-3 mb-2">
              <div><strong>{{ (permanentDeleteTarget.first_name || '') + ' ' + (permanentDeleteTarget.last_name || '') || permanentDeleteTarget.user_name }}</strong></div>
              <div class="text-caption grey--text">{{ permanentDeleteTarget.email }}</div>
              <div class="text-caption grey--text">{{ permanentDeleteTarget.phone }}</div>
            </div>
            <p class="text-caption error--text mb-0">Their phone and email will be freed for re-registration.</p>
            <v-alert v-if="permanentDeleteError" dense text type="error" class="mt-3 mb-0">{{ permanentDeleteError }}</v-alert>
          </v-card-text>
          <v-card-actions class="px-4 pb-3">
            <v-spacer></v-spacer>
            <v-btn text @click="showPermanentDeleteDialog = false; permanentDeleteTarget = null; permanentDeleteError = ''" :disabled="isPermanentDeleting">Cancel</v-btn>
            <v-btn color="error" @click="confirmPermanentDelete" :loading="isPermanentDeleting">
              <v-icon left small>mdi-delete-forever</v-icon>
              Permanently Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog.vue";
export default {
  name: "doctorsPage",
  components: { DeleteConfirmationDialog },
  data() {
    return {
      filter: {
        search_keyword: "",
        is_active: "",
        is_verified: "",
        position_id: 1, // DOCTOR position (confirmed by getMasters)
        show_deleted: false,
      },
      pendingVerificationCount: 0,
      page: 1,
      itemsPerPage: 10,
      selectedItemsPerPage: 10,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      jumpToPage: null,
      dateMenu: false,
      dateRange: [],
      is_deleted_user: false,
      confirmDeleteDialog: false,
      deleteTargetItem: null,
      deleteDialog: false,
      itemToDelete: null,
      deleteLoading: false,
      showPermanentDeleteDialog: false,
      permanentDeleteTarget: null,
      permanentDeleteError: "",
      showRestoreDialog: false,
      restoreTarget: null,
      restoreError: "",
      isRestoring: false,
      isPermanentDeleting: false,
      users: [],
      statuses: [
        { text: "Active", value: "1" },
        { text: "Inactive", value: "0" },
      ],
      verificationOptions: [
        { text: "Verified", value: "1" },
        { text: "Pending Verification", value: "0" },
      ],
      positions: [],
      headers: [
        {
          text: "#",
          value: "sr_no",
          sortable: false,
          width: "50px",
          align: "center",
        },
        {
          text: "Doctor Info",
          value: "user_info",
          sortable: false,
          width: "160px",
        },
        { text: "Contact", value: "contact", sortable: false, width: "140px" },
        {
          text: "Role & Position",
          value: "role_position",
          sortable: false,
          width: "120px",
        },
        { text: "Wallet", value: "wallet", sortable: false, width: "90px" },
        { text: "Status", value: "is_active", sortable: false, width: "80px" },
        { text: "Verification", value: "verification", sortable: false, width: "100px", align: "center" },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          width: "160px",
          align: "center",
        },
      ],
      isLoading: false,
      isExcelLoading: false,
      showVerifyDialog: false,
      doctorToVerify: null,
      verifyRemarks: '',
      isVerifying: false,
    };
  },
  computed: {
    hasActiveFilters() {
      return (
        this.filter.search_keyword ||
        this.filter.is_active ||
        this.filter.is_verified
      );
    },
  },
  watch: {
    page: "getData",
    itemsPerPage: "getData",
    $route() {
      this.getData();
    },
  },
  mounted() {
    this.getData();           // fire data request first
    this.fetchPendingCount(this.filter.position_id);
    this.getMasters();        // fetch masters concurrently for dropdowns
  },
  methods: {
    async getMasters() {
      try {
        const response = await axios.get(`masters?context=user`);
        const masters = response.data;

        this.positions =
          masters.positions?.map((position) => ({
            text: position.name,
            value: position.id,
          })) || [];

        const doctorPosition = masters.positions?.find(p => p.name === 'DOCTOR');
        if (doctorPosition && this.filter.position_id !== doctorPosition.id) {
          // position_id changed from default — reload with correct ID
          this.filter.position_id = doctorPosition.id;
          this.getData();
          this.fetchPendingCount(doctorPosition.id);
        }
      } catch (error) {
        console.error('Error loading masters data:', error);
        
        // Handle specific API context errors
        if (error.response?.status === 400 && error.response?.data?.error === 'Invalid or missing context') {
          console.error('Masters API Context Error:', error.response.data);
          this.$toast?.error('Invalid API configuration. Please contact support.');
        } else if (error.response?.status === 401) {
          window.location.href = "/auth/login";
        } else {
          this.$toast?.error('Failed to load configuration data');
        }
      }
    },
    async getData() {
      console.log("Get Data - Doctors");
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;
      this.isLoading = true;
      if (this.$route.query.is_active !== undefined && this.filter.is_active === "") {
        this.filter.is_active = this.$route.query.is_active;
      }
      this.filter.search_keyword = this.filter.search_keyword
        ? this.filter.search_keyword
        : "";
      this.filter.is_active = this.filter.is_active
        ? this.filter.is_active
        : "";
      this.filter.position_id = this.filter.position_id
        ? this.filter.position_id
        : "";
      let query =
        "lite=1&page=" +
        this.page +
        "&rowsPerPage=" +
        this.itemsPerPage +
        "&show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&is_verified=" +
        (this.filter.is_verified ?? "") +
        "&position_id=" +
        this.filter.position_id +
        "&search_keyword=" +
        this.filter.search_keyword;
      if (this.$route.query.registration_completed !== undefined) {
        query += "&registration_completed=" + this.$route.query.registration_completed;
      }
      if (this.$route.query.is_verified_pathy !== undefined) {
        query += "&is_verified_pathy=" + this.$route.query.is_verified_pathy;
      }
      await axios
        .get(`users?${query}`)
        .then((response) => {
          this.users = response.data.data;
          this.count = response.data.count;
          this.serialNoStarting = (this.page - 1) * this.itemsPerPage;
          this.paginationLength = Math.ceil(this.count / this.itemsPerPage);
        })
        .catch(function (error) {
          console.log(error);
        });
      this.isLoading = false;
    },

    openVerifyDialog(doctor) {
      this.doctorToVerify = doctor;
      this.verifyRemarks = '';
      this.showVerifyDialog = true;
    },

    closeVerifyDialog() {
      this.showVerifyDialog = false;
      this.doctorToVerify = null;
      this.verifyRemarks = '';
      this.isVerifying = false;
    },

    async submitVerify() {
      if (!this.doctorToVerify) return;
      this.isVerifying = true;
      try {
        const action = this.doctorToVerify.is_active ? 'reject' : 'approve';
        await axios.post(`/users/${this.encryptIt(this.doctorToVerify.id)}/verify`, {
          action,
          remarks: this.verifyRemarks || undefined,
        });
        this.closeVerifyDialog();
        this.getData();
      } catch (error) {
        console.error(error);
      } finally {
        this.isVerifying = false;
      }
    },

    openDeleteConfirm(item) {
      this.deleteTargetItem = item;
      this.confirmDeleteDialog = true;
    },

    openDeleteDialog(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      this.deleteLoading = true;
      try {
        await axios.post(`/users/delete/${this.encryptIt(this.itemToDelete.id)}`);
        this.deleteDialog = false;
        this.itemToDelete = null;
        this.getData();
      } catch (error) {
        console.log(error);
      }
      this.deleteLoading = false;
    },

    async Delete_this(id) {
      this.delete_user = true;
      await axios
        .post(`/users/delete/${this.encryptIt(id)}`)
        .then(() => {
          this.delete_user = false;
          this.confirmDeleteDialog = false;
          this.deleteTargetItem = null;
          this.is_deleted_user = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          window.location.href = "/auth/login";
        });
    },
    openPermanentDeleteConfirm(item) {
      this.permanentDeleteTarget = item;
      this.permanentDeleteError = '';
      this.showPermanentDeleteDialog = true;
    },

    async confirmPermanentDelete() {
      if (!this.permanentDeleteTarget) return;
      this.isPermanentDeleting = true;
      this.permanentDeleteError = '';
      try {
        await axios.post(`/users/permanent-delete/${this.encryptIt(this.permanentDeleteTarget.id)}`);
        this.showPermanentDeleteDialog = false;
        this.permanentDeleteTarget = null;
        await this.getData();
      } catch (error) {
        console.error('Error permanently deleting doctor:', error);
        if (error?.response?.status === 401) {
          window.location.href = '/auth/login';
        } else {
          this.permanentDeleteError =
            error?.response?.data?.message || 'Failed to permanently delete this record.';
        }
      } finally {
        this.isPermanentDeleting = false;
      }
    },

    async generateExcel() {
      this.isExcelLoading = true;
      try {
        const params = new URLSearchParams();
        if (this.filter.position_id) params.append('position_id', this.filter.position_id);
        if (this.filter.search_keyword) params.append('search_keyword', this.filter.search_keyword);
        if (this.filter.is_active !== '' && this.filter.is_active !== null) params.append('is_active', this.filter.is_active);
        if (this.filter.is_verified !== '' && this.filter.is_verified !== null) params.append('is_verified', this.filter.is_verified);
        params.append('show_deleted', this.filter.show_deleted ? 1 : 0);

        const response = await axios.get(`users/excel/doctors?${params.toString()}`, {
          responseType: 'blob',
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;

        const contentDisposition = response.headers['content-disposition'];
        const today = new Date().toISOString().slice(0, 10);
        let filename = `doctors_export_${today}.xlsx`;
        if (contentDisposition) {
          const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
          if (match && match[1]) filename = match[1].replace(/['"]/g, '');
        }

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Excel export failed:', error);
        this.$toast.error('Failed to export doctors');
      } finally {
        this.isExcelLoading = false;
      }
    },

    isDeletePage() {
      let response = false;
      if (this.$route.query.open_trash) {
        response = true;
      }
      return response;
    },

    openTrash() {
      this.$router
        .push({ query: { ...this.$route.query, open_trash: 1 } })
        .catch(() => {});
    },

    closeTrash() {
      const query = { ...this.$route.query };
      delete query.open_trash;
      this.$router.push({ query }).catch(() => {});
    },

    openRestoreConfirm(item) {
      this.restoreTarget = item;
      this.restoreError = '';
      this.showRestoreDialog = true;
    },

    async confirmRestore() {
      if (!this.restoreTarget) return;
      this.isRestoring = true;
      this.restoreError = '';
      try {
        await axios.post(`/users/restore/${this.encryptIt(this.restoreTarget.id)}`);
        this.showRestoreDialog = false;
        this.restoreTarget = null;
        this.$toast?.success?.('Doctor restored successfully.');
        await this.getData();
      } catch (error) {
        console.error('Error restoring doctor:', error);
        if (error?.response?.status === 401) {
          window.location.href = '/auth/login';
        } else {
          this.restoreError = error?.response?.data?.message || 'Failed to restore this doctor.';
        }
      } finally {
        this.isRestoring = false;
      }
    },

    // Enhanced functionality methods
    getFullName(user) {
      if (!user) return "Unknown User";
      const firstName = user.first_name || "";
      const lastName = user.last_name || "";
      return `${firstName} ${lastName}`.trim() || user.email || "No name";
    },

    getImageUrl(imagePath) {
      if (!imagePath) return "";
      const base = (this.mediaUrl || '').replace(/\/$/, '');
      return `${base}/${imagePath}`;
    },

    getWalletColor(amount) {
      const wallet = parseFloat(amount) || 0;
      if (wallet >= 1000) return "success";
      if (wallet >= 500) return "warning";
      return "error";
    },

    formatCurrency(amount) {
      const wallet = parseFloat(amount) || 0;
      return wallet.toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },

    async fetchPendingCount(positionId) {
      try {
        const pid = positionId || this.filter.position_id;
        const res = await axios.get(`users?is_verified=0&is_active=1&position_id=${pid}&count_only=1`);
        this.pendingVerificationCount = res.data.count || 0;
      } catch (e) {
        this.pendingVerificationCount = 0;
      }
    },

    filterByPending() {
      this.filter.is_verified = "0";
      this.page = 1;
      this.getData();
    },

    clearAllFilters() {
      this.filter.search_keyword = "";
      this.filter.is_active = "";
      this.filter.is_verified = "";
      // Keep position_id as DOCTOR
      this.page = 1;
      this.getData();
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.search_keyword) count++;
      if (this.filter.is_active) count++;
      if (this.filter.is_verified) count++;
      return count;
    },

    changeItemsPerPageToggle() {
      this.itemsPerPage = this.selectedItemsPerPage;
      this.page = 1;
      this.getData();
    },

    goToPage() {
      const targetPage = parseInt(this.jumpToPage);
      if (
        targetPage &&
        targetPage >= 1 &&
        targetPage <= this.paginationLength
      ) {
        this.page = targetPage;
        this.getData();
      }
      this.jumpToPage = null;
    },
  },
};
</script>

<style scoped>
/* Compact Layout Adjustments */
.v-container {
  max-width: 100% !important;
}

/* Enhanced Table Styling - Compact Version */
.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 8px;
  font-size: 0.75rem !important;
}

.enhanced-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  font-weight: 600;
}

.enhanced-table >>> .v-data-table-header th {
  padding: 8px 6px !important;
  border-bottom: 1px solid #e0e0e0 !important;
  font-size: 0.75rem !important;
  height: 36px !important;
}

.enhanced-table >>> tbody tr:hover {
  background: rgba(25, 118, 210, 0.04) !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody tr {
  height: 44px !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody td {
  padding: 6px 6px !important;
  vertical-align: middle !important;
  border-bottom: 1px solid #f5f5f5 !important;
  font-size: 0.75rem !important;
  height: 44px !important;
}

/* Compact Filter Cards */
.v-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
}

.v-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

/* Compact Button Styling */
.v-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.2px;
  font-size: 0.75rem !important;
  min-height: 28px !important;
}

.v-btn--x-small {
  height: 24px !important;
  min-width: 48px !important;
  font-size: 0.7rem !important;
  padding: 0 8px !important;
}

.v-btn--small {
  height: 28px !important;
  min-width: 56px !important;
  font-size: 0.75rem !important;
  padding: 0 12px !important;
}

.v-btn-toggle >>> .v-btn {
  min-width: 32px !important;
  height: 24px !important;
  font-size: 0.7rem !important;
  padding: 0 6px !important;
}

/* Compact Pagination */
.compact-pagination >>> .v-pagination__item {
  height: 28px !important;
  min-width: 32px !important;
  width: auto !important;
  padding: 0 6px !important;
  font-size: 0.75rem !important;
  margin: 0 1px !important;
}

.compact-pagination >>> .v-pagination__navigation {
  height: 28px !important;
  width: 28px !important;
  min-width: 28px !important;
}

.compact-input >>> .v-input__control .v-input__slot {
  min-height: 24px !important;
}

.compact-input >>> .v-input__control .v-input__slot input {
  padding: 2px 6px !important;
  font-size: 0.7rem !important;
}

/* Compact Chip Styling */
.v-chip {
  font-weight: 500;
  letter-spacing: 0.1px;
  font-size: 0.7rem !important;
  height: 20px !important;
}

.v-chip--x-small {
  height: 18px !important;
  font-size: 0.65rem !important;
}

.v-chip .v-icon {
  font-size: 12px !important;
}

/* Compact Avatar Styling */
.v-avatar {
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

/* Compact Menu Styling */
.v-menu__content {
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.v-list-item {
  min-height: 32px !important;
  padding: 0 12px !important;
}

.v-list-item__icon {
  margin-right: 8px !important;
  width: 16px !important;
}

.v-list-item__title {
  font-size: 0.75rem !important;
}

/* Progress Enhancements */
.v-progress-circular {
  filter: drop-shadow(0 2px 4px rgba(25, 118, 210, 0.3));
}

/* Responsive Design - Even More Compact on Mobile */
@media (max-width: 768px) {
  .enhanced-table >>> tbody td {
    padding: 4px 4px !important;
    font-size: 0.7rem !important;
  }

  .enhanced-table >>> .v-data-table-header th {
    padding: 6px 4px !important;
    font-size: 0.7rem !important;
  }

  .v-card-title {
    padding: 8px 12px 4px !important;
  }

  .v-card-text {
    padding: 4px 12px 8px !important;
  }

  .v-card-actions {
    padding: 4px 12px !important;
  }

  .v-card-subtitle {
    padding: 4px 12px !important;
  }
}

/* No spin arrows for number input */
.no-spin-arrows >>> input[type="number"]::-webkit-outer-spin-button,
.no-spin-arrows >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.no-spin-arrows >>> input[type="number"] {
  -moz-appearance: textfield;
}

/* Table Loading Animation */
.v-data-table--loading {
  position: relative;
}

.v-data-table--loading::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(2px);
  z-index: 10;
}

/* Reduce overall spacing */
.mb-6 {
  margin-bottom: 16px !important;
}

/* Compact row spacing */
[dense] .row {
  margin: -4px !important;
}

[dense] .row > .col,
[dense] .row > [class*="col-"] {
  padding: 4px !important;
}
</style>